import * as z from "zod/mini";
import type { ConfigValidationFieldError } from "@moonpay/platform-protocol/models/connection";
import {
  BOOT_FORCE_ERROR_SCENARIOS,
  OTP_FORCE_ERROR_SCENARIOS,
  CONNECTION_FORCE_ERROR_SCENARIOS,
} from "@connect/data/mocks/force-error-scenarios";
import { FORCED_KYC_SCENARIOS } from "@connect/data/mocks/forced-kyc-scenarios";
import {
  apiTargetHeader,
  devBaseSchema,
  parseFrameConfig,
  themeBaseSchema,
} from "../../lib/parse-frame-config";

const publicSchema = z.extend(themeBaseSchema, {
  clientToken: z._default(z.string(), ""),
  publicKey: z._default(z.string(), ""),
  channelId: z._default(z.string(), ""),
  autoFocus: z.catch(z._default(z.stringbool(), true), true),
});

const devSchema = z.object({
  ...devBaseSchema.shape,
  screen: z._default(z.string(), ""),
  // Shortens the verification poll timeout so the timeout path can be exercised quickly.
  pollTimeoutMs: z.catch(z.optional(z.coerce.number()), undefined),
  usOnboarding: z.catch(z._default(z.stringbool(), false), false),
  forceError: z._default(
    z.enum([
      "",
      ...[
        ...BOOT_FORCE_ERROR_SCENARIOS,
        ...OTP_FORCE_ERROR_SCENARIOS,
        ...CONNECTION_FORCE_ERROR_SCENARIOS,
        ...FORCED_KYC_SCENARIOS,
      ].map((s) => s.value),
    ]),
    "",
  ),
});

const KNOWN_KEYS = new Set([...Object.keys(publicSchema.shape), ...Object.keys(devSchema.shape)]);

export type FrameConfig = z.infer<typeof publicSchema> & z.infer<typeof devSchema>;

export function onboardingClientHeaders(config: FrameConfig): Record<string, string> {
  const headers = apiTargetHeader(config);
  if (config.forceError) {
    headers["x-mock-scenario"] = config.forceError;
  }
  return headers;
}

export function parseConfig(search?: string): FrameConfig {
  const { config, devConfig } = parseFrameConfig({
    publicSchema,
    devSchema,
    knownKeys: KNOWN_KEYS,
    search,
  });
  return { ...config, ...devConfig };
}

export const US_ONBOARDING = parseConfig().usOnboarding;

export const DEFAULT_COOLDOWN_S = 60;

export function extractCooldown(data: { retryAfterSeconds: number } | undefined): number {
  return data?.retryAfterSeconds ?? DEFAULT_COOLDOWN_S;
}

const X25519_HEX = /^(0x)?[0-9a-fA-F]{64}$/;

export function validateConfig(config: FrameConfig): ConfigValidationFieldError[] {
  const errors: ConfigValidationFieldError[] = [];
  if (!config.clientToken) {
    errors.push({
      code: "invalidClientToken",
      message: "clientToken is required",
    });
  }
  if (!config.publicKey) {
    errors.push({
      code: "invalidPublicKey",
      message: "publicKey is required",
    });
  } else if (!X25519_HEX.test(config.publicKey)) {
    errors.push({
      code: "invalidPublicKey",
      message: "publicKey must be 64 hex characters (32 bytes) representing an X25519 public key",
    });
  }
  return errors;
}
